import { ShieldCheck, Award, Handshake, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES, BRAND } from "@/lib/brand";

export default function AboutPage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-[#2D181A] text-[#FFFDF8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            About · हमारे बारे में
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 leading-[1.02] max-w-4xl">
            The most{" "}
            <span className="italic text-[#D4AF37]">trustworthy</span> address
            for cash-for-gold in North India.
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#FFFDF8]/80 leading-relaxed max-w-3xl">
            Top Rate was built for one reason — customers were tired of hidden
            deductions, weight games and dealers who quoted one rate and paid
            another. We stand for the opposite: publish the rate, run the
            karat test in the open, and pay the number we quoted.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              Our Story
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-4 leading-[1.1]">
              A family of{" "}
              <span className="italic text-[#641C24]">gold valuers</span> —
              working with Gurgaon families for years.
            </h2>
            <div className="mt-6 space-y-5 text-[#4A3B3C] leading-relaxed text-base">
              <p>
                Top Rate operates from two locations in Gurgaon — the bustling{" "}
                <span className="text-[#2D181A] font-semibold">
                  Sadar Bazaar
                </span>{" "}
                and the premium{" "}
                <span className="text-[#2D181A] font-semibold">
                  Sapphire Mall on Sector 49
                </span>
                . Our valuers are officially recognised by the government,
                allowing us to issue certificates accepted by banks, insurance
                companies and courts.
              </p>
              <p>
                Whether you are unlocking a family heirloom for an emergency,
                selling old ornaments to fund a new life event, or clearing a
                mortgage against gold — Top Rate is designed to feel like a
                bank, not a bazaar. Clean counters, digital scales in your
                sight, XRF karat testing, and one final rate.
              </p>
              <p>
                We serve customers across{" "}
                <span className="text-[#641C24] font-semibold">
                  Gurgaon, Delhi NCR, Noida, Faridabad, Ghaziabad, Chandigarh
                  and the entire North India belt
                </span>{" "}
                — including a home-visit option for senior citizens and larger
                consignments.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={IMAGES.ornament1}
              alt="Traditional Indian gold ornament"
              loading="lazy"
              className="w-full h-96 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#FAF6EC] tr-grain border-y border-[#E5DCC5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
            What We Stand For
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Four values that keep customers coming back.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              {
                Icon: ShieldCheck,
                title: "Transparency",
                hindi: "पारदर्शिता",
                body: "Every step — karat test, weight, deductions, final payout — happens on your side of the counter.",
              },
              {
                Icon: Award,
                title: "Certification",
                hindi: "प्रमाणित",
                body: "Govt. approved valuers with valuation certificates accepted by banks and courts.",
              },
              {
                Icon: TrendingUp,
                title: "Best Rate",
                hindi: "सबसे अच्छी दर",
                body: "We benchmark daily against MCX + IBJA rates. If someone quotes higher, tell us — we'll match or beat.",
              },
              {
                Icon: Handshake,
                title: "Respect",
                hindi: "सम्मान",
                body: "Selling gold is emotional. Our team explains everything patiently — in English, Hindi or Punjabi.",
              },
            ].map((v) => (
              <div
                key={v.title}
                className="bg-white border border-[#E5DCC5] p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-11 h-11 border border-[#D4AF37] flex items-center justify-center text-[#641C24]">
                  <v.Icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl font-bold mt-5">
                  {v.title}
                </h3>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#641C24] mt-1">
                  {v.hindi}
                </div>
                <p className="text-sm text-[#4A3B3C] leading-relaxed mt-4">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="bg-[#641C24] text-[#FFFDF8] p-10 md:p-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05]">
                Ready to get the{" "}
                <span className="italic text-[#D4AF37]">best rate</span> for
                your gold?
              </h2>
              <p className="text-[#FFFDF8]/80 mt-4 leading-relaxed">
                Visit a branch or call now — {BRAND.phone}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#D4AF37] hover:bg-[#B8860B] text-[#2D181A] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider"
              >
                Contact Us
              </Link>
              <Link
                to="/branches"
                className="inline-flex items-center justify-center border border-[#D4AF37]/70 hover:bg-[#D4AF37] hover:text-[#2D181A] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider"
              >
                Find a Branch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
